import { ColDef, ColumnState } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { getTabGridOptions } from './components/ColumnDefinitions';
import { defaultTQC2020_R01Dto, TQC2020_R01Dto } from './Dto_TQC2020_R01';
import useTQC2020_R01Store, { TabType } from './Store_TQC2020_R01';

interface StockGridBaseProps {
	gridRef: React.RefObject<AgGridReact<any> | null>;
	tabType: TabType;
	data: any[];
	columns: ColDef[];
	onTabChange: (tab: TabType) => Promise<void>;
}

const BOTTOM_LABEL_COLS = new Set(['itemCd', 'workplaceNm', 'compNm']);

const normalizeHeaderClass = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { children?: ColDef[] }) => {
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass].filter(Boolean).join(' ');
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true, children };
	});

const applyPinnedCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		const prevCellStyle = def.cellStyle;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) => {
				if (params?.node?.rowPinned === 'bottom') {
					return {
						...(typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle),
						textAlign: 'center',
					};
				}
				return typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle;
			},
		};
	});

export const Table_TQC2020_R01 = ({
	gridRef,
	tabType,
	data,
	columns,
	onTabChange,
}: StockGridBaseProps) => {
	const {
		activeTab,
		tabColumnStates,
		setTabColumnState,
		tabSelectedIds,
		setTabSelectedIds,
		tabScrollTop,
		setTabScrollTop,
	} = useTQC2020_R01Store();

	const [gridKey, setGridKey] = useState(0);

	useEffect(() => { setGridKey((prev) => prev + 1); }, [tabType]);

	const localStorageKey = useMemo(
		() => `TQC2020_R01_${tabType}_columns`
	, [tabType]);

	const tabs = useMemo(() => ([
		{ id: 'date' as const, label: '일자별 불량' },
		{ id: 'item' as const, label: '품목별 불량' },
		{ id: 'workplace' as const, label: '공정별 불량' },
	]), []);

	const handleTabClick = useCallback(async (tab: TabType) => {
		if (tab === activeTab) return;
		await onTabChange(tab);
	}, [onTabChange, activeTab]);

	const getRowId = useCallback((params: any) => {
		const rowData = params.data;
		switch (tabType) {
			case 'date':
				return `${rowData.compNm}-${rowData.inspectDt}-${rowData.workplaceNm}-${rowData.itemCd}-${rowData.inspectorEmpNm}-${rowData.badCaseNm}`;
			case 'item':
				return `${rowData.compNm}-${rowData.itemCd}-${rowData.workplaceNm}-${rowData.inspectDt}-${rowData.inspectorEmpNm}-${rowData.badCaseNm}`;
			case 'workplace':
				return `${rowData.compNm}-${rowData.workplaceNm}-${rowData.itemCd}-${rowData.inspectDt}-${rowData.inspectorEmpNm}-${rowData.badCaseNm}`;
			default:
				return rowData.id || params.node.id;
		}
	}, [tabType]);

	const makeRowId = useCallback((rowData: any) => {
		switch (tabType) {
			case 'date':
				return `${rowData.compNm}-${rowData.inspectDt}-${rowData.workplaceNm}-${rowData.itemCd}-${rowData.inspectorEmpNm}-${rowData.badCaseNm}`;
			case 'item':
				return `${rowData.compNm}-${rowData.itemCd}-${rowData.workplaceNm}-${rowData.inspectDt}-${rowData.inspectorEmpNm}-${rowData.badCaseNm}`;
			case 'workplace':
				return `${rowData.compNm}-${rowData.workplaceNm}-${rowData.itemCd}-${rowData.inspectDt}-${rowData.inspectorEmpNm}-${rowData.badCaseNm}`;
			default:
				return rowData?.id;
		}
	}, [tabType]);

	const baseColumnDefs = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		...columns,
	], [columns]);

	const effectiveColumnState = useMemo(() => {
		const savedState = tabColumnStates[tabType];
		return Array.isArray(savedState) && savedState.length === 0 ? null : savedState;
	}, [tabColumnStates, tabType]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs = useMemo(() => {
		const mergedColumnDefs = effectiveColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs({
				baseColumnDefs,
				columnState: effectiveColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyPinnedCenterAlign(normalizeHeaderClass(mergedColumnDefs), BOTTOM_LABEL_COLS);
	}, [baseColumnDefs, effectiveColumnState]);

	const localDefaultColDef = useMemo(() => ({
		...defaultColDef,
		suppressMenu: true,
		headerClass: aggridHeaderClass,
	}), []);

	const rowClassRules = useMemo(() => {
		const selectedList = tabSelectedIds[tabType] || [];
		return {
			selected: (params: any) => {
				if (params.node.rowPinned === 'bottom') return false;
				const id = makeRowId(params.data);
				return selectedList.includes(id);
			},
			hover: (params: any) => {
				if (params.node.rowPinned === 'bottom') return false;
				if (!params.data) return false;
				const id = makeRowId(params.data);
				return !selectedList.includes(id);
			},
		} as Record<string, (p: any) => boolean>;
	}, [tabSelectedIds, tabType, makeRowId]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		selectedIds: tabSelectedIds[tabType] || [],
		setSelectedIds: (ids: Array<string | number>) => setTabSelectedIds(tabType, ids.map(String)),
		resolveId: (row: TQC2020_R01Dto) => makeRowId(row),
	}), [tabSelectedIds, tabType, setTabSelectedIds, makeRowId]);

	const pinnedBottomRowData = useMemo(() => {
		if (!Array.isArray(data) || data.length === 0) return [] as any[];

		const number = (v: any) => Number(v) || 0;

		return [{
			itemCd: `${data.length.toLocaleString()}건`,
			inspectQty: data.reduce((s, r: any) => s + number(r.inspectQty), 0),
			badQty: data.reduce((s, r: any) => s + number(r.badQty), 0),
			acceptQty: data.reduce((s, r: any) => s + number(r.acceptQty), 0),
		}];
	}, [data]);

	return (
		<div className="w-full h-full flex flex-col">
			<div
				className="flex items-end mb-[0.1px]  bg-white h-[25px] "
				role="tablist"
				aria-label="생산실적 조회 탭"
			>
				{tabs.map((tab) => {
					const isActive = activeTab === (tab.id as TabType);
					return (
						<button
							key={tab.id}
							onClick={() => handleTabClick(tab.id as TabType)}
							role="tab"
							aria-selected={isActive}
							className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
								${isActive
									? 'bg-white text-black-700 -mb-px shadow-sm'
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
							`}
						>
							{tab.label}
						</button>
					);
				})}
			</div>

			<div className="flex-1">
				<GridContextWrapper<TQC2020_R01Dto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={data}
					initSelected={() => {}}
					defaultRowData={defaultTQC2020_R01Dto}
					baseColumnDefs={effectiveColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={(state: ColumnState[] | null) => setTabColumnState(tabType, state || [])}
					localStorageKey={localStorageKey}
					menuItems={undefined}
				>
					<Grid_Excel<TQC2020_R01Dto>
						key={gridKey}
						ref={gridRef as any}
						rowData={data}
						columnDefs={columnDefs}
						defaultColDef={localDefaultColDef}
						getRowId={getRowId}
						rowClassRules={rowClassRules}
						pinnedBottomRowData={pinnedBottomRowData}
						shiftRangeSelection={shiftRangeSelectionConfig}
						columnStateKey={localStorageKey}
						setColumnState={(state: ColumnState[]) => setTabColumnState(tabType, state || [])}
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={tabScrollTop[tabType]}
						setScrollTop={(top) => setTabScrollTop(tabType, top)}
						{...getTabGridOptions()}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
